/**
 * dcoded-llp — single-flight locks for cart ADD + shared cart.js reads.
 * Blocks theme duplicates and app endpoints (addtocart, add-to-carts, /cart/add).
 */
(function () {
  if (window.__dcodedLlpCartAddGuard) return;
  window.__dcodedLlpCartAddGuard = true;

  /** @type {Promise<Response> | null} */
  var addInFlight = null;
  /** @type {Promise<Response> | null} */
  var cartJsInFlight = null;
  var blockedUntil = 0;
  var activeToken = '';
  var COOLDOWN_MS = 4000;

  function isNotificationMode() {
    var t = (window.Theme && window.Theme.cartType) || '';
    if (t === 'notification' || t === 'notification_drawer') return true;
    var body = document.body;
    if (!body) return false;
    if (body.classList.contains('dcoded-llp-notification-mode')) return true;
    var mode = body.getAttribute('data-dcoded-llp-cart-mode') || '';
    return mode === 'notification' || mode === 'notification_drawer';
  }

  function normalizeUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url || '';
    return String((input && input.url) || '');
  }

  function isCartJsUrl(url) {
    if (typeof url !== 'string') return false;
    var path = url.split('?')[0].toLowerCase();
    return path === '/cart.js' || path.slice(-8) === '/cart.js' || path.slice(-7) === 'cart.js';
  }

  /** Any request that can ADD a line item (theme + apps). */
  function isCartAddUrl(url) {
    if (typeof url !== 'string') return false;
    var u = url.toLowerCase();
    if (u.indexOf('/cart/add') !== -1) return true;
    if (u.indexOf('addtocart') !== -1) return true;
    if (u.indexOf('add-to-cart') !== -1) return true;
    if (u.indexOf('add_to_cart') !== -1) return true;
    if (u.indexOf('cart/add') !== -1) return true;
    // Storefront API cartLinesAdd style paths sometimes used by apps
    if (u.indexOf('cartlinesadd') !== -1 || u.indexOf('cart_lines_add') !== -1) return true;
    return false;
  }

  function getHeader(init, name) {
    if (!init || !init.headers) return '';
    var h = init.headers;
    if (typeof h.get === 'function') return h.get(name) || '';
    return h[name] || h[name.toLowerCase()] || '';
  }

  function isThemeRequest(input, init) {
    if (getHeader(init, 'X-Dcoded-Llp-Atc') === '1') return true;
    if (input instanceof Request) {
      try {
        return input.headers.get('X-Dcoded-Llp-Atc') === '1';
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  function getToken(input, init) {
    var token = getHeader(init, 'X-Dcoded-Llp-Atc-Token');
    if (token) return token;
    if (input instanceof Request) {
      try {
        return input.headers.get('X-Dcoded-Llp-Atc-Token') || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  function blockDuplicate() {
    return Promise.reject(new DOMException('Duplicate cart add blocked by dcoded-llp', 'AbortError'));
  }

  /**
   * One-shot token for the theme POST.
   * Returns '' if an add is already in flight / on cooldown.
   * @param {{ bypassCooldown?: boolean }} [options]
   *   bypassCooldown — cart-drawer upsell: allow a second intentional add
   *   right after the first product (cooldown must not swallow that click).
   */
  window.__dcodedLlpIssueAtcToken = function (options) {
    var bypassCooldown = Boolean(options && options.bypassCooldown);

    if (addInFlight || window.__dcodedLlpAtcBusy) {
      return '';
    }

    if (!bypassCooldown && Date.now() < blockedUntil) {
      return '';
    }

    // Clear stale cooldown so the matching fetch() wrapper also allows this add.
    if (bypassCooldown) {
      blockedUntil = 0;
    }

    activeToken = 'llp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    window.__dcodedLlpExpectThemeAdd = Date.now();
    return activeToken;
  };

  /** Landing sticky ATC → checkout: clear stale locks and always mint a fresh token. */
  window.__dcodedLlpForceAtcToken = function () {
    addInFlight = null;
    blockedUntil = 0;
    window.__dcodedLlpAtcBusy = false;
    activeToken = 'llp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    window.__dcodedLlpExpectThemeAdd = Date.now();
    return activeToken;
  };

  window.__dcodedLlpAtcBusy = false;

  document.addEventListener(
    'pointerdown',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          'product-form-component button[type="submit"], product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"], [data-dcoded-pdp-sticky-cta], [data-dcoded-llp-sticky-cta], .add-to-cart-button, [data-dcoded-llp-form-submit]'
        )
      ) {
        // Marks the click so app XHR/fetch cannot add in the same gesture
        window.__dcodedLlpExpectThemeAdd = Date.now();
      }
    },
    true
  );

  var nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var url = normalizeUrl(input);

    // Share in-flight /cart.js so drawer open doesn't double-fetch
    if (isCartJsUrl(url)) {
      if (cartJsInFlight) {
        return cartJsInFlight.then(function (response) {
          return response.clone();
        });
      }
      cartJsInFlight = nativeFetch(input, init).finally(function () {
        var done = cartJsInFlight;
        window.setTimeout(function () {
          if (cartJsInFlight === done) cartJsInFlight = null;
        }, 150);
      });
      return cartJsInFlight.then(function (response) {
        return response.clone();
      });
    }

    if (!isCartAddUrl(url)) {
      return nativeFetch(input, init);
    }

    if (addInFlight || Date.now() < blockedUntil || window.__dcodedLlpAtcBusy) {
      return blockDuplicate();
    }

    var theme = isThemeRequest(input, init);
    var token = getToken(input, init);

    // In notification modes ONLY the theme-marked tokenized request may add
    if (isNotificationMode()) {
      if (!theme || !token || token !== activeToken) {
        return blockDuplicate();
      }
      activeToken = '';
    } else if (!theme) {
      if (window.__dcodedLlpExpectThemeAdd && Date.now() - window.__dcodedLlpExpectThemeAdd < 2500) {
        return blockDuplicate();
      }
    }

    blockedUntil = Date.now() + COOLDOWN_MS;
    window.__dcodedLlpAtcBusy = true;

    addInFlight = nativeFetch(input, init)
      .then(function (response) {
        window.__dcodedLlpExpectThemeAdd = 0;
        return response;
      })
      .catch(function (err) {
        blockedUntil = Date.now() + 500;
        throw err;
      })
      .finally(function () {
        var done = addInFlight;
        window.setTimeout(function () {
          if (addInFlight === done) {
            addInFlight = null;
            window.__dcodedLlpAtcBusy = false;
          }
        }, 50);
      });

    return addInFlight.then(function (response) {
      return response.clone();
    });
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dcodedLlpUrl = String(url || '');
    this.__dcodedLlpCartAdd = isCartAddUrl(this.__dcodedLlpUrl);
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.__dcodedLlpCartAdd) {
      // Apps never carry our token — block while notification mode / busy / cooling down
      if (
        isNotificationMode() ||
        addInFlight ||
        Date.now() < blockedUntil ||
        window.__dcodedLlpAtcBusy ||
        (window.__dcodedLlpExpectThemeAdd && Date.now() - window.__dcodedLlpExpectThemeAdd < 4000)
      ) {
        try {
          this.abort();
        } catch (e) {
          /* ignore */
        }
        return;
      }
      blockedUntil = Date.now() + COOLDOWN_MS;
      window.__dcodedLlpAtcBusy = true;
      var xhr = this;
      xhr.addEventListener('loadend', function () {
        window.setTimeout(function () {
          window.__dcodedLlpAtcBusy = false;
        }, 50);
      });
    }
    return nativeSend.apply(this, arguments);
  };
})();
